import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FinishRoute } from '~analytics/types';
import { translate } from '~l10n/client';
import activity from 'activity';
import Backbone from 'backbone';
import { Provider } from 'react-redux';
import store from 'store';

import {
  InviteStudentDrawer,
  InviteStudentDrawerContext,
} from '~teacher/components/ActivityResult/modals/InviteStudentDrawer';
import { queryClient } from '~teacher/queryClient';

import PopupController from '../../../../shared/components/popup/PopupController';
import { ErrorView } from '../../../shared/error/ErrorView';
import reduxStore from '../../../src/store';
import HeaderController from '../../header/HeaderController';
import SpaceRaceModel from '../models/SpaceRaceModel';
import SpaceRaceView from '../views/SpaceRaceView';

let router = null;

class SpaceRaceController {
  doSpaceRace(teacherRouter) {
    router = teacherRouter;
    activity.refresh(controller.activityRefreshed);
    if (SpaceRaceModel.getMinHeight() === -1) {
      window.addEventListener('resize', controller.setMinHeight);
    }
  }

  activityRefreshed() {
    if (activity.isSpaceRace() && activity.isLive()) {
      SpaceRaceModel.buildContext(controller.contextBuilt);
      controller.renderHeader();
    } else {
      router.routeToLaunch();
    }
  }

  contextBuilt() {
    if (!SpaceRaceModel.isListening()) {
      SpaceRaceModel.startListening(controller.activityChanged);
    }
    controller.setMinHeight();
    controller.renderView();
  }

  activityChanged() {
    if (activity.isSpaceRace() && Backbone.history.fragment === 'space-race') {
      SpaceRaceModel.buildContext(controller.contextBuilt);
    } else {
      SpaceRaceModel.endListening();
    }
  }

  setMinHeight() {
    if (Backbone.history.fragment !== 'space-race') {
      window.removeEventListener('resize', controller.setMinHeight);
      SpaceRaceModel.setMinHeight(-1);
      return;
    }

    let pageContainer = document.getElementById('page-container').offsetHeight;
    let bannerContainer =
      document.getElementById('banner-container').offsetHeight;
    let headerContainer = document.getElementById('header').offsetHeight;

    let backgroundMinHeight = pageContainer - bannerContainer - headerContainer;
    SpaceRaceModel.setMinHeight(backgroundMinHeight);
    controller.renderView();
  }

  renderView() {
    if (activity.isSpaceRace() && Backbone.history.fragment === 'space-race') {
      let countdownColor = 'green';

      if (activity.getCountdownMinutes() === 0) {
        countdownColor = activity.getCountdownSeconds() > 0 ? 'red' : 'orange';
      }

      ReactDOM.render(
        <ErrorView>
          <Provider store={reduxStore}>
            <QueryClientProvider client={queryClient}>
              <InviteStudentDrawerContext>
                <SpaceRaceView
                  minHeight={SpaceRaceModel.getMinHeight()}
                  context={SpaceRaceModel.get('context')}
                  showCountdown={activity.shouldShowCountdown()}
                  countdownColor={countdownColor}
                  countdownText={
                    activity.isCountdownDone()
                      ? translate('Space Race complete!')
                      : translate('Space Race time remaining')
                  }
                  countdownTime={activity.getCountdownDisplay()}
                  stopCountdownClicked={controller.stopCountdownClicked}
                  showCountdownFinish={activity.isCountdownDone()}
                  finishClicked={controller.finishClicked}
                />
              </InviteStudentDrawerContext>
            </QueryClientProvider>
          </Provider>
        </ErrorView>,
        document.getElementById('main-content'),
        controller.checkCountdown
      );
    }
  }

  checkCountdown() {
    if (activity.shouldShowCountdown()) {
      activity.addCountdownCallback(controller.countdownChanged);
      if (activity.shouldStartCountdown()) {
        activity.startCountdown();
      }
    }
  }

  countdownChanged() {
    if (activity.isCountdownDone()) {
      controller.finishByCountdown();
    }
    controller.renderView();
  }

  stopCountdownClicked() {
    if (!activity.isCountdownDone()) {
      PopupController.render({
        title: translate('Please Confirm'),
        message: translate('Are you sure you want to stop the countdown?'),
        buttonText: translate('Yes'),
        cancelText: translate('No'),
        buttonClicked: controller.stopCountdown,
      });
    } else {
      controller.stopCountdown();
    }
  }

  stopCountdown() {
    store.set('countdownStopped', true);
    activity.resetCountdown();
    controller.renderView();
  }

  finishByCountdown() {
    SpaceRaceModel.endListening();
    SpaceRaceModel.setFinishedByCountdown(true);
  }

  renderHeader() {
    if (Backbone.history.fragment === 'space-race') {
      HeaderController.render('results');
    }
  }

  finishClicked() {
    const activityInstanceId = activity.get('id');
    const quizId = activity.get('quiz').id;
    const routeToFinalResultsTable = () => {
      router.routeToFinalResultsTable({
        activityId: activityInstanceId,
        quizId: quizId,
      });
    };
    PopupController.render({
      title: translate('Confirm'),
      message: translate('Are you sure you want to end the current activity?'),
      cancelText: translate('No'),
      buttonClicked: () => {
        if (activity.isCountdownDone()) {
          activity.clear();
          routeToFinalResultsTable();
        } else {
          activity.endCurrentActivity(
            routeToFinalResultsTable,
            false,
            FinishRoute.ResultsPage
          );
        }
      },
    });
  }
}

let controller = new SpaceRaceController();
module.exports = controller;
